window.ProductsPage = function ProductsPage({ 
  openQuoteModal, 
  selectedCategory, 
  setSelectedCategory,
  categoriesList = ['All', 'Railways', 'Industrial Automation', 'Aerospace', 'Automotive', 'Custom CAD & Fixtures'],
  setActiveTab,
  setSelectedProductSlug
}) {
  const [productsList, setProductsList] = React.useState([]);
  const [categories, setCategories] = React.useState(categoriesList);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchProducts();
    fetchCategories();

    const handleSync = () => {
      fetchProducts();
      fetchCategories();
    };

    window.addEventListener('novatek_content_updated', handleSync);
    window.addEventListener('storage', handleSync);

    if (window.SEOHelper) {
      window.SEOHelper.updatePageMeta(
        "Industrial Test Rigs & Automation Systems Catalogue | Novatek",
        "Browse Novatek's complete catalogue of custom test rigs, automated test benches, pneumatic validation stations, and robotic automation cells."
      );
    }

    return () => {
      window.removeEventListener('novatek_content_updated', handleSync);
      window.removeEventListener('storage', handleSync);
    };
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await window.apiClient.get('products.php');
      if (res.success && res.data) {
        setProductsList(res.data);
      }
    } catch (e) {
      console.warn('Products fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await window.apiClient.get('categories.php');
      if (res.success && res.data && res.data.length > 0) {
        const catNames = res.data.map(c => c.name);
        setCategories(['All', ...catNames]);
      }
    } catch (e) {
      // Keep default categoriesList
    }
  };

  const filteredProducts = productsList.filter((p) => {
    const pTitle = p.title || p.name || '';
    const pDesc = p.shortDesc || p.short_description || '';
    const pCat = p.category || p.category_name || '';
    const matchesCat = selectedCategory === 'All' || pCat.toLowerCase() === selectedCategory.toLowerCase();
    const matchesSearch = pTitle.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          pDesc.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const handleOpenProduct = (product) => {
    if (setSelectedProductSlug) {
      setSelectedProductSlug(product.slug || product.id);
    }
    if (setActiveTab) {
      setActiveTab('product-detail');
    }
  };

  return (
    <div className="py-12 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="font-mono text-xs sm:text-sm font-bold text-secondary tracking-widest uppercase block">
            Custom Test & Automation Systems
          </span>
          <h1 className="font-sans text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight">
            Engineering Product Catalogue
          </h1>
          <div className="h-1 w-20 bg-secondary mx-auto rounded-full"></div>
          <p className="font-sans text-slate-600 text-base sm:text-lg">
            High-capacity dynamic test rigs, automated pneumatic/hydraulic validation benches, and turnkey robotic assembly cells.
          </p>
        </div>

        {/* Redesigned Clean Search & Category Filter Bar */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex flex-col lg:flex-row gap-4 justify-between items-stretch lg:items-center">
            
            {/* Category Segmented Pills */}
            <div className="flex flex-wrap items-center gap-2 flex-1">
              {categories.map((cat) => {
                const count = cat === 'All' 
                  ? productsList.length 
                  : productsList.filter(p => (p.category === cat || p.category_name === cat || (cat === 'Railways' && (p.sector === 'railway' || p.category === 'Railway Test Systems')))).length;
                const isSelected = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2.5 rounded-xl font-sans text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                      isSelected
                        ? 'bg-secondary text-white shadow-md shadow-orange-500/20'
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200/80'
                    }`}
                  >
                    <span>{cat}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-sans font-bold ${
                      isSelected ? 'bg-white/20 text-white' : 'bg-slate-200/70 text-slate-600'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Integrated Search Input */}
            <div className="relative w-full lg:w-80 shrink-0">
              <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-base">
                search
              </span>
              <input
                type="text"
                placeholder="Search test systems, rigs, valves..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-9 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-sans outline-none focus:border-secondary focus:bg-white transition-all shadow-2xs"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-xs p-1"
                >
                  ✕
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Product Cards Grid */}
        {loading ? (
          <div className="p-16 text-center text-slate-400 font-mono text-xs">Loading machine catalogue from backend...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-slate-200 p-8 space-y-4">
            <div className="h-16 w-16 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto">
              <span className="material-symbols-outlined text-4xl">inventory_2</span>
            </div>
            <h3 className="font-sans text-xl font-bold text-slate-800">No Matching Machines Found</h3>
            <p className="font-sans text-slate-500 text-sm max-w-sm mx-auto leading-relaxed">
              Try adjusting your search criteria or contact our engineering team directly to discuss custom machine specifications.
            </p>
            <button
              onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}
              className="bg-secondary text-white text-xs font-mono font-bold px-4 py-2 rounded-xl uppercase"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => {
              const imageSrc = window.resolveMediaUrl(product.primary_image || product.image, 'machine');
              return (
                <div
                  key={product.id || product.slug}
                  onClick={() => handleOpenProduct(product)}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group cursor-pointer"
                >
                  {/* Image Container */}
                  <div>
                    <div className="h-56 bg-slate-900 overflow-hidden relative">
                      <img
                        src={imageSrc}
                        alt={product.title || product.name}
                        onError={(e) => window.handleImageError(e, 'machine')}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <span className="absolute top-3 left-3 bg-slate-900/85 backdrop-blur-xs text-secondary text-[11px] font-mono font-bold px-2.5 py-1 rounded border border-orange-500/30">
                        {product.category || product.category_name || 'Test System'}
                      </span>
                      {product.badge && (
                        <span className="absolute top-3 right-3 bg-slate-900/85 text-white text-[10px] font-mono px-2 py-0.5 rounded">
                          {product.badge}
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-3">
                      <h3 className="font-sans text-xl font-bold text-slate-900 group-hover:text-secondary transition-colors line-clamp-2">
                        {product.title || product.name}
                      </h3>
                      <p className="font-sans text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-3">
                        {product.shortDesc || product.short_description}
                      </p>
                    </div>
                  </div>

                  {/* Actions Bar */}
                  <div className="p-6 pt-0 space-y-3">
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenProduct(product);
                        }}
                        className="flex-1 bg-slate-900 group-hover:bg-secondary text-white text-xs font-mono font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5"
                      >
                        <span>View Engineering Dossier</span>
                        <span className="material-symbols-outlined text-sm">arrow_forward</span>
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openQuoteModal();
                        }}
                        className="p-2.5 rounded-xl border border-slate-300 hover:border-secondary text-slate-700 hover:text-secondary transition-colors"
                        title="Request Quote for this Machine"
                      >
                        <span className="material-symbols-outlined text-base">engineering</span>
                      </button>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
};
